import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Target, Users, Award, TrendingUp, Globe, Shield, 
  Lightbulb, Rocket, Heart, Star, CheckCircle, 
  Zap, Database, Code, Cpu, Cloud, Lock, ArrowRight,
  Building2, Briefcase, Sparkles, ChevronDown, ChevronUp,
  MapPin, Calendar, Clock, Target as TargetIcon,
  BarChart3, Layers, CpuIcon as CpuIcon
} from 'lucide-react';

export default function About() {
  const [activeTimelineIndex, setActiveTimelineIndex] = useState(0);
  const [isStatsVisible, setIsStatsVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  // Track scroll progress for animations
  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
      const currentScroll = window.scrollY;
      setScrollProgress((currentScroll / totalScroll) * 100);
      
      if (currentScroll > 300) {
        setIsStatsVisible(true);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ============================================================================
  // Animated Counter Component
  // ============================================================================
  const AnimatedCounter = ({ value, suffix = '', duration = 2000 }) => {
    const [count, setCount] = useState(0);
    
    useEffect(() => {
      if (isStatsVisible) {
        let start = 0;
        const end = parseInt(value);
        const increment = Math.ceil(end / (duration / 16)); // 60fps
        
        const timer = setInterval(() => {
          start += increment;
          if (start >= end) {
            setCount(end);
            clearInterval(timer);
          } else {
            setCount(start);
          }
        }, 16);
        
        return () => clearInterval(timer);
      }
    }, [value, duration, isStatsVisible]);
    
    return (
      <span className="inline-block">
        {count}{suffix}
      </span>
    );
  };

  // ============================================================================
  // Core Values Data (Memoized)
  // ============================================================================
  const values = useMemo(() => [
    {
      icon: <Lightbulb className="w-10 h-10 md:w-12 md:h-12" />,
      title: "Innovation First",
      description: "Pioneering cutting-edge solutions that push technological boundaries",
      gradient: "bg-gradient-to-br from-yellow-500 via-orange-500 to-red-500",
      color: "text-yellow-600 dark:text-yellow-400",
      delay: "0s"
    },
    {
      icon: <Heart className="w-10 h-10 md:w-12 md:h-12" />,
      title: "Customer-Centric",
      description: "Your success is our mission. We build lasting partnerships",
      gradient: "bg-gradient-to-br from-red-500 via-pink-500 to-rose-500",
      color: "text-red-600 dark:text-red-400",
      delay: "0.1s"
    },
    {
      icon: <Shield className="w-10 h-10 md:w-12 md:h-12" />,
      title: "Quality Assurance",
      description: "Uncompromising standards in every project we deliver",
      gradient: "bg-gradient-to-br from-blue-500 via-cyan-500 to-teal-500",
      color: "text-blue-600 dark:text-blue-400",
      delay: "0.2s"
    },
    {
      icon: <Globe className="w-10 h-10 md:w-12 md:h-12" />,
      title: "Global Reach",
      description: "Local expertise with international standards and practices",
      gradient: "bg-gradient-to-br from-green-500 via-emerald-500 to-lime-500",
      color: "text-green-600 dark:text-green-400",
      delay: "0.3s"
    }
  ], []);

  // ============================================================================
  // Milestones Data (Memoized)
  // ============================================================================
  const milestones = useMemo(() => [
    { 
      year: "March 2025", 
      event: "First 10 Clients", 
      description: "Initial client acquisition that inspired company formation",
      icon: "🚀",
      delay: "0s"
    },
    { 
      year: "April 2025", 
      event: "50+ Projects Milestone", 
      description: "Successfully completed 50+ projects as independent consultants",
      icon: "🎯",
      delay: "0.1s"
    },
    { 
      year: "May 2025", 
      event: "Company Founded", 
      description: "Officially established NEVXOAULIO VENTURES on 5th May 2025",
      icon: "🏢",
      delay: "0.2s"
    },
    { 
      year: "June 2025", 
      event: "Team Expansion", 
      description: "Grew to 15+ skilled professionals across multiple technologies",
      icon: "👥",
      delay: "0.3s"
    },
    { 
      year: "July 2025", 
      event: "250+ Happy Clients", 
      description: "Reached milestone of 250+ satisfied clients",
      icon: "⭐",
      delay: "0.4s"
    },
    { 
      year: "Future", 
      event: "Global Expansion Planning", 
      description: "Planning international operations and government contracts",
      icon: "🌍",
      delay: "0.5s"
    }
  ], []);

  // ============================================================================
  // Expertise Data (Memoized)
  // ============================================================================
  const expertise = useMemo(() => [
    { 
      icon: <Code className="w-8 h-8 md:w-10 md:h-10" />, 
      name: "Software Development", 
      projects: "15+", 
      gradient: "from-blue-500 to-blue-600",
      color: "text-blue-600 dark:text-blue-400",
      delay: "0s"
    },
    { 
      icon: <Cloud className="w-8 h-8 md:w-10 md:h-10" />, 
      name: "Cloud Computing", 
      projects: "5+", 
      gradient: "from-cyan-500 to-cyan-600",
      color: "text-cyan-600 dark:text-cyan-400",
      delay: "0.1s"
    },
    { 
      icon: <Lock className="w-8 h-8 md:w-10 md:h-10" />, 
      name: "Cyber Security", 
      projects: "5+", 
      gradient: "from-red-500 to-red-600",
      color: "text-red-600 dark:text-red-400",
      delay: "0.2s"
    },
    { 
      icon: <Cpu className="w-8 h-8 md:w-10 md:h-10" />, 
      name: "VLSI Design", 
      projects: "50+", 
      gradient: "from-purple-500 to-purple-600",
      color: "text-purple-600 dark:text-purple-400",
      delay: "0.3s"
    },
    { 
      icon: <Database className="w-8 h-8 md:w-10 md:h-10" />, 
      name: "AI & ML", 
      projects: "25+", 
      gradient: "from-green-500 to-green-600",
      color: "text-green-600 dark:text-green-400",
      delay: "0.4s"
    },
    { 
      icon: <Zap className="w-8 h-8 md:w-10 md:h-10" />, 
      name: "Data Analytics", 
      projects: "10+", 
      gradient: "from-orange-500 to-orange-600",
      color: "text-orange-600 dark:text-orange-400",
      delay: "0.5s"
    }
  ], []);

  // ============================================================================
  // Team Structure Data (Memoized)
  // ============================================================================
  const team = useMemo(() => [
    {
      role: "Leadership Team",
      count: "5+",
      description: "Visionary leaders with 10+ years industry experience",
      icon: <Briefcase className="w-10 h-10 md:w-12 md:h-12" />,
      gradient: "from-blue-500 to-purple-500",
      delay: "0s"
    },
    {
      role: "Senior Engineers",
      count: "10+",
      description: "Expert developers and architects driving innovation",
      icon: <Code className="w-10 h-10 md:w-12 md:h-12" />,
      gradient: "from-purple-500 to-pink-500",
      delay: "0.1s"
    },
    {
      role: "Project Managers",
      count: "5+",
      description: "Certified PMs ensuring on-time delivery",
      icon: <TargetIcon className="w-10 h-10 md:w-12 md:h-12" />,
      gradient: "from-pink-500 to-rose-500",
      delay: "0.2s"
    },
    {
      role: "Support Staff",
      count: "2+",
      description: "24/7 dedicated support team",
      icon: <Users className="w-10 h-10 md:w-12 md:h-12" />,
      gradient: "from-teal-500 to-cyan-500",
      delay: "0.3s"
    }
  ], []);

  // ============================================================================
  // Achievements Data (Memoized)
  // ============================================================================
  const achievements = useMemo(() => [
    { 
      text: "DPIIT Startup India Recognized", 
      icon: "🇮🇳",
      color: "bg-gradient-to-br from-orange-50 to-yellow-50 dark:from-orange-900/20 dark:to-yellow-900/20",
      border: "border-orange-200 dark:border-orange-800",
      delay: "0s"
    },
    { 
      text: "MSME Registered Enterprise", 
      icon: "📋",
      color: "bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20",
      border: "border-blue-200 dark:border-blue-800",
      delay: "0.1s"
    },
    { 
      text: "Google Partner Verified", 
      icon: "🔍",
      color: "bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20",
      border: "border-red-200 dark:border-red-800",
      delay: "0.2s"
    },
    { 
      text: "AWS Certified Solutions", 
      icon: "☁️",
      color: "bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20",
      border: "border-amber-200 dark:border-amber-800",
      delay: "0.3s"
    },
    { 
      text: "100% Client Satisfaction", 
      icon: "⭐",
      color: "bg-gradient-to-br from-yellow-50 to-amber-50 dark:from-yellow-900/20 dark:to-amber-900/20",
      border: "border-yellow-200 dark:border-yellow-800",
      delay: "0.4s"
    },
    { 
      text: "Zero Data Breach Record", 
      icon: "🔒",
      color: "bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20",
      border: "border-green-200 dark:border-green-800",
      delay: "0.5s"
    }
  ], []);

  // ============================================================================
  // Industries Data (Memoized)
  // ============================================================================
  const industries = useMemo(() => [
    { name: "Healthcare", icon: "🏥", color: "text-blue-600" },
    { name: "Finance & Banking", icon: "💰", color: "text-green-600" },
    { name: "E-commerce", icon: "🛒", color: "text-orange-600" },
    { name: "Education", icon: "📚", color: "text-purple-600" },
    { name: "Manufacturing", icon: "🏭", color: "text-red-600" },
    { name: "Logistics", icon: "🚚", color: "text-teal-600" },
    { name: "Real Estate", icon: "🏢", color: "text-indigo-600" },
    { name: "Government", icon: "🏛️", color: "text-gray-600" },
    { name: "Automotive", icon: "🚗", color: "text-blue-600" },
    { name: "Telecommunications", icon: "📡", color: "text-purple-600" },
    { name: "Energy", icon: "⚡", color: "text-yellow-600" },
    { name: "Agriculture", icon: "🌾", color: "text-green-600" }
  ], []);

  // ============================================================================
  // Stats Data (Memoized)
  // ============================================================================
  const stats = useMemo(() => [
    { value: "50", label: "Projects Delivered", suffix: "+", icon: <Rocket className="w-10 h-10 md:w-12 md:h-12" />, delay: "0s" },
    { value: "250", label: "Happy Clients", suffix: "+", icon: <Users className="w-10 h-10 md:w-12 md:h-12" />, delay: "0.1s" },
    { value: "15", label: "Team Members", suffix: "+", icon: <Award className="w-10 h-10 md:w-12 md:h-12" />, delay: "0.2s" },
    { value: "24", label: "Support Hours", suffix: "/7", icon: <Shield className="w-10 h-10 md:w-12 md:h-12" />, delay: "0.3s" }
  ], []);

  // ============================================================================
  // Why Choose Us Data (Memoized)
  // ============================================================================
  const whyChooseUs = useMemo(() => [
    { 
      icon: <Award className="w-8 h-8 md:w-10 md:h-10" />, 
      title: "Proven Track Record", 
      desc: "50+ successful projects delivered",
      color: "from-yellow-500 to-orange-500",
      delay: "0s"
    },
    { 
      icon: <Users className="w-8 h-8 md:w-10 md:h-10" />, 
      title: "Expert Team", 
      desc: "Certified professionals with years of experience",
      color: "from-blue-500 to-cyan-500",
      delay: "0.1s"
    },
    { 
      icon: <TrendingUp className="w-8 h-8 md:w-10 md:h-10" />, 
      title: "Scalable Solutions", 
      desc: "Built to grow with your business",
      color: "from-green-500 to-emerald-500",
      delay: "0.2s"
    },
    { 
      icon: <Shield className="w-8 h-8 md:w-10 md:h-10" />, 
      title: "Security First", 
      desc: "Zero data breach record maintained",
      color: "from-red-500 to-pink-500",
      delay: "0.3s"
    },
    { 
      icon: <Star className="w-8 h-8 md:w-10 md:h-10" />, 
      title: "Quality Standards", 
      desc: "Industry-leading best practices",
      color: "from-purple-500 to-violet-500",
      delay: "0.4s"
    },
    { 
      icon: <Zap className="w-8 h-8 md:w-10 md:h-10" />, 
      title: "Fast Delivery", 
      desc: "Agile methodology for quick turnaround",
      color: "from-orange-500 to-amber-500",
      delay: "0.5s"
    }
  ], []);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 overflow-hidden">
      
      {/* Scroll Progress Bar */}
      <div className="fixed top-0 left-0 right-0 h-1 z-50">
        <div 
          className="h-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* ===================================================================
          SECTION 1: Hero Section
          =================================================================== */}
      <section className="relative pt-24 pb-20 md:pt-32 md:pb-28 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-blue-400 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-400 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>
        
        {/* Floating Particles */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-white/30 rounded-full animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${10 + Math.random() * 10}s`
              }}
            />
          ))}
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 md:gap-16 items-center">
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6 animate-slide-in-left">
                <Sparkles className="w-4 h-4 mr-2" />
                <span className="text-sm md:text-base font-medium">Making India Self-Reliant in Technology 🇮🇳</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight tracking-tight">
                <span className="block bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">
                  About NEVXOAULIO
                </span>
                <span className="block text-3xl sm:text-4xl md:text-5xl mt-4 text-blue-200">
                  Ventures
                </span>
              </h1>
              
              <p className="text-lg sm:text-xl md:text-2xl text-blue-100 mb-8 leading-relaxed">
                Pioneering technological excellence and innovation across multiple domains with cutting-edge solutions
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/contact" 
                  className="group px-8 py-4 bg-white text-blue-600 rounded-xl font-semibold hover:bg-gray-50 hover:scale-105 active:scale-95 transition-all duration-300 shadow-lg hover:shadow-2xl flex items-center justify-center text-center animate-slide-in-up"
                  aria-label="Work with us"
                  style={{ animationDelay: '0.2s' }}
                >
                  <span>Work With Us</span>
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link 
                  to="/portfolio" 
                  className="group px-8 py-4 bg-transparent border-2 border-white rounded-xl font-semibold hover:bg-white/10 hover:scale-105 active:scale-95 transition-all duration-300 shadow-lg hover:shadow-2xl flex items-center justify-center text-center animate-slide-in-up"
                  aria-label="View our portfolio"
                  style={{ animationDelay: '0.3s' }}
                >
                  <span>Our Work</span>
                  <ChevronDown className="ml-2 w-5 h-5 group-hover:translate-y-1 transition-transform" />
                </Link>
              </div>
            </div>
            
            <div className="hidden lg:block animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop"
                  alt="Team collaboration"
                  className="rounded-3xl shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute -top-6 -right-6 w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-2xl flex items-center justify-center text-white text-4xl shadow-2xl animate-float">
                  🚀
                </div>
                <div className="absolute -bottom-6 -left-6 w-20 h-20 bg-gradient-to-br from-green-400 to-teal-500 rounded-2xl flex items-center justify-center text-white text-3xl shadow-2xl animate-float" style={{ animationDelay: '1s' }}>
                  ⭐
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================================================================
          SECTION 2: Stats Section
          =================================================================== */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <div 
                key={index}
                className="group bg-white dark:bg-gray-900 p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 animate-fade-in-up hover:-translate-y-2 border border-gray-100 dark:border-gray-800"
                style={{ animationDelay: stat.delay }}
              >
                <div className="flex justify-center text-blue-600 dark:text-blue-400 mb-4 transform group-hover:scale-110 transition-transform">
                  {stat.icon}
                </div>
                <div className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-2 text-center font-mono">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-gray-600 dark:text-gray-400 text-center text-sm sm:text-base md:text-lg font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================================================================
          SECTION 3: Vision & Mission
          =================================================================== */}
      <section className="py-16 md:py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12">
            <div 
              className="group bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-600 p-8 md:p-12 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 animate-fade-in-up hover:-translate-y-2"
            >
              <div className="inline-flex p-4 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl mb-6 group-hover:scale-110 transition-transform">
                <Target className="w-8 h-8 md:w-10 md:h-10 text-white" />
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
                Our <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Vision</span>
              </h2>
              <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed">
                To establish India as a global technology powerhouse by building world-class solutions 
                that compete on the international stage. We envision an India that is not just a consumer 
                of technology, but a creator and innovator driving the fourth industrial revolution.
              </p>
              <div className="mt-8 flex items-center space-x-2 text-blue-600 dark:text-blue-400">
                <MapPin className="w-5 h-5" />
                <span className="text-sm font-medium">Global Technology Leadership</span>
              </div>
            </div>

            <div 
              className="group bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-600 p-8 md:p-12 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-500 animate-fade-in-up hover:-translate-y-2"
              style={{ animationDelay: '0.1s' }}
            >
              <div className="inline-flex p-4 bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl mb-6 group-hover:scale-110 transition-transform">
                <Rocket className="w-8 h-8 md:w-10 md:h-10 text-white" />
              </div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
                Our <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">Mission</span>
              </h2>
              <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 leading-relaxed">
                To deliver cutting-edge technology solutions that empower businesses to achieve their goals. 
                We are committed to fostering innovation, ensuring quality, and building sustainable 
                partnerships that create value for our clients and contribute to nation-building.
              </p>
              <div className="mt-8 flex items-center space-x-2 text-purple-600 dark:text-purple-400">
                <TargetIcon className="w-5 h-5" />
                <span className="text-sm font-medium">Innovation & Excellence</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================================================================
          SECTION 4: Core Values
          =================================================================== */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full mb-4">
              <Sparkles className="w-4 h-4 mr-2 text-white" />
              <span className="text-sm font-medium text-white">Guiding Principles</span>
            </div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
              Our <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">Core Values</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              The principles that guide every decision and action we take
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {values.map((value, index) => (
              <div 
                key={index}
                className="group relative bg-white dark:bg-gray-900 p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 animate-fade-in-up hover:-translate-y-3 border border-gray-100 dark:border-gray-800"
                style={{ animationDelay: value.delay }}
              >
                <div className={`absolute inset-0 ${value.gradient} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-500`} />
                
                <div className={`relative z-10 w-16 h-16 md:w-20 md:h-20 ${value.gradient} rounded-2xl flex items-center justify-center text-white mb-6 transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                  {value.icon}
                </div>
                
                <h3 className={`text-xl md:text-2xl font-bold mb-3 ${value.color} group-hover:scale-105 transition-transform`}>
                  {value.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                  {value.description}
                </p>
                
                <div className="mt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="h-1 w-12 bg-gradient-to-r from-transparent via-current to-transparent" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================================================================
          SECTION 5: Journey Timeline
          =================================================================== */}
      <section className="py-16 md:py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full mb-4">
              <Calendar className="w-4 h-4 mr-2 text-white" />
              <span className="text-sm font-medium text-white">Our Growth Story</span>
            </div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
              Our <span className="bg-gradient-to-r from-amber-600 via-orange-600 to-red-600 bg-clip-text text-transparent">Journey</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Milestones that shaped our growth and define our future
            </p>
          </div>

          <div className="relative max-w-5xl mx-auto">
            {/* Timeline Line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 md:w-2 bg-gradient-to-b from-blue-500 via-purple-500 to-pink-500 hidden lg:block"></div>
            
            <div className="space-y-12 lg:space-y-16">
              {milestones.map((milestone, index) => (
                <div 
                  key={index}
                  className={`relative flex flex-col lg:flex-row items-center animate-fade-in-up ${index % 2 === 0 ? 'lg:justify-start' : 'lg:justify-end'}`}
                  style={{ animationDelay: milestone.delay }}
                  onMouseEnter={() => setActiveTimelineIndex(index)}
                >
                  <div className={`w-full lg:w-5/12 ${index % 2 === 0 ? 'lg:text-right lg:pr-12' : 'lg:text-left lg:pl-12'}`}>
                    <div className={`group bg-white dark:bg-gray-800 p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 ${activeTimelineIndex === index ? 'ring-2 ring-blue-500 scale-105' : ''}`}>
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-2xl transform group-hover:scale-110 transition-transform">{milestone.icon}</span>
                        <span className="text-sm font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-3 py-1 rounded-full">
                          {milestone.year}
                        </span>
                      </div>
                      
                      <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-3">
                        {milestone.event}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                        {milestone.description}
                      </p>
                      
                      <div className="mt-4 flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-500 dark:text-gray-400">Milestone Achieved</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Timeline Dot */}
                  <div className="absolute left-1/2 transform -translate-x-1/2 w-8 h-8 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full border-4 border-white dark:border-gray-900 hidden lg:flex items-center justify-center">
                    <div className="w-2 h-2 bg-white rounded-full" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===================================================================
          SECTION 6: Expertise Areas
          =================================================================== */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full mb-4">
              <Layers className="w-4 h-4 mr-2 text-white" />
              <span className="text-sm font-medium text-white">Technical Excellence</span>
            </div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
              Our <span className="bg-gradient-to-r from-teal-600 via-emerald-600 to-green-600 bg-clip-text text-transparent">Expertise</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Specialized services across multiple technology domains with proven results
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {expertise.map((area, index) => (
              <div 
                key={index}
                className="group relative bg-white dark:bg-gray-900 p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 animate-fade-in-up hover:-translate-y-3 border border-gray-100 dark:border-gray-800"
                style={{ animationDelay: area.delay }}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className={`w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br ${area.gradient} rounded-2xl flex items-center justify-center text-white transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                    {area.icon}
                  </div>
                  <div className={`text-3xl md:text-4xl font-bold ${area.color} transform group-hover:scale-110 transition-transform`}>
                    {area.projects}
                  </div>
                </div>
                
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white mb-3">
                  {area.name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 uppercase tracking-wider font-semibold mb-2">
                  Projects Completed
                </p>
                
                <div className="mt-6 h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                  <div 
                    className={`h-full bg-gradient-to-r ${area.gradient} rounded-full transform translate-x-[-100%] group-hover:translate-x-0 transition-transform duration-1000`}
                    style={{ width: '100%' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================================================================
          SECTION 7: Team Structure
          =================================================================== */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-4">
              <Users className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Our Greatest Asset</span>
            </div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 leading-tight">
              Our <span className="bg-gradient-to-r from-white via-blue-100 to-purple-100 bg-clip-text text-transparent">Team</span>
            </h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">
              Talented professionals driving innovation and excellence every day
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {team.map((group, index) => (
              <div 
                key={index}
                className="group bg-white/10 backdrop-blur-sm p-6 md:p-8 rounded-2xl border border-white/20 hover:bg-white/20 hover:scale-105 transition-all duration-500 animate-fade-in-up"
                style={{ animationDelay: group.delay }}
              >
                <div className={`mb-6 w-16 h-16 md:w-20 md:h-20 bg-gradient-to-br ${group.gradient} rounded-2xl flex items-center justify-center text-white transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                  {group.icon}
                </div>
                
                <div className="text-4xl md:text-5xl font-bold mb-2 font-mono">
                  {group.count}
                </div>
                
                <h3 className="text-xl md:text-2xl font-bold mb-3">
                  {group.role}
                </h3>
                
                <p className="text-blue-100 text-sm md:text-base leading-relaxed">
                  {group.description}
                </p>
                
                <div className="mt-6 h-1 w-12 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================================================================
          SECTION 8: Industries Served
          =================================================================== */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full mb-4">
              <Globe className="w-4 h-4 mr-2 text-white" />
              <span className="text-sm font-medium text-white">Cross-Industry Expertise</span>
            </div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
              Industries We <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">Serve</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Delivering transformative solutions across diverse sectors and domains
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-6">
            {industries.map((industry, index) => (
              <div 
                key={index}
                className="group bg-white dark:bg-gray-900 p-4 md:p-6 rounded-xl shadow-md hover:shadow-2xl text-center hover:scale-105 transition-all duration-300 animate-fade-in-up border border-gray-100 dark:border-gray-800"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="text-4xl md:text-5xl mb-3 transform group-hover:scale-110 transition-transform">
                  {industry.icon}
                </div>
                <p className={`font-semibold text-sm md:text-base ${industry.color} dark:text-white group-hover:scale-105 transition-transform`}>
                  {industry.name}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================================================================
          SECTION 9: Achievements
          =================================================================== */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-full mb-4">
              <Award className="w-4 h-4 mr-2 text-white" />
              <span className="text-sm font-medium text-white">Accolades & Recognition</span>
            </div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
              Our <span className="bg-gradient-to-r from-amber-600 via-yellow-600 to-orange-600 bg-clip-text text-transparent">Achievements</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Recognition and certifications that validate our commitment to excellence
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {achievements.map((achievement, index) => (
              <div 
                key={index}
                className={`group ${achievement.color} ${achievement.border} p-6 md:p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 animate-fade-in-up hover:-translate-y-2`}
                style={{ animationDelay: achievement.delay }}
              >
                <div className="flex items-center space-x-4 md:space-x-6">
                  <span className="text-4xl md:text-5xl transform group-hover:scale-110 transition-transform flex-shrink-0">
                    {achievement.icon}
                  </span>
                  <span className="text-gray-900 dark:text-white font-semibold text-base md:text-lg group-hover:scale-105 transition-transform">
                    {achievement.text}
                  </span>
                </div>
                
                <div className="mt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <CheckCircle className="w-5 h-5 text-green-500 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================================================================
          SECTION 10: Why Choose Us
          =================================================================== */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-purple-600 via-indigo-600 to-blue-700 text-white relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-white/10 to-transparent"></div>
          <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-white/10 to-transparent"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full mb-4">
              <Star className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">The NEVXOAULIO Advantage</span>
            </div>
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-4 leading-tight">
              Why Choose <span className="bg-gradient-to-r from-white via-purple-100 to-pink-100 bg-clip-text text-transparent">NEVXOAULIO?</span>
            </h2>
            <p className="text-xl text-purple-100 max-w-3xl mx-auto">
              What sets us apart in the competitive technology landscape
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {whyChooseUs.map((item, index) => (
              <div 
                key={index}
                className="group bg-white/10 backdrop-blur-sm p-6 md:p-8 rounded-2xl border border-white/20 hover:bg-white/20 hover:scale-105 transition-all duration-500 animate-fade-in-up"
                style={{ animationDelay: item.delay }}
              >
                <div className={`w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br ${item.color} rounded-2xl flex items-center justify-center mb-6 text-white transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                  {item.icon}
                </div>
                
                <h3 className="text-xl md:text-2xl font-bold mb-3">
                  {item.title}
                </h3>
                
                <p className="text-purple-100 text-sm md:text-base leading-relaxed">
                  {item.desc}
                </p>
                
                <div className="mt-6 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="h-1 w-12 bg-gradient-to-r from-transparent via-white to-transparent" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================================================================
      SECTION 11: CTA Section (Fixed - No Fast Blinking)
      =================================================================== */}
  <section className="py-16 md:py-24 relative overflow-hidden">
    {/* Calm, Subtle Gradient Background */}
    <div className="absolute inset-0 bg-gradient-to-br from-blue-50/80 via-indigo-50/60 to-purple-50/80 
                    dark:from-gray-900/95 dark:via-gray-800/95 dark:to-gray-900/95">
      
      {/* Subtle Pattern Overlay */}
      <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '60px 60px'
          }}>
      </div>
      
      {/* Gentle Glow Effects (Very Slow) */}
      <div className="absolute top-1/4 -left-32 w-96 h-96 bg-blue-300/5 dark:bg-blue-500/5 
                      rounded-full blur-3xl animate-soft-pulse"></div>
      <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-purple-300/5 dark:bg-purple-500/5 
                      rounded-full blur-3xl animate-soft-pulse" style={{animationDelay: '3s'}}></div>
      
    </div>
    
    {/* Subtle Floating Elements (Very Slow) */}
    <div className="absolute inset-0 overflow-hidden">
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="absolute w-8 h-8 md:w-12 md:h-12 opacity-5 dark:opacity-10"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `float-very-slow 20s ease-in-out infinite`,
            animationDelay: `${i * 4}s`,
            background: `conic-gradient(from ${i * 72}deg at 50% 50%, 
                        rgba(59, 130, 246, 0.3) 0deg, 
                        rgba(139, 92, 246, 0.3) 90deg, 
                        rgba(236, 72, 153, 0.3) 180deg, 
                        rgba(59, 130, 246, 0.3) 270deg)`,
            borderRadius: i % 2 === 0 ? '50%' : '30%'
          }}
        />
      ))}
    </div>
    
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
      
      {/* Icon with Gentle Glow */}
      <div className="inline-flex p-4 md:p-5 bg-gradient-to-br from-blue-500 to-purple-500 
                      rounded-2xl mb-8 shadow-lg shadow-blue-500/20 dark:shadow-blue-900/30">
        <Building2 className="w-10 h-10 md:w-12 md:h-12 text-white" />
      </div>
      
      {/* Main Heading */}
      <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
        Ready to <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 
                                bg-clip-text text-transparent">Transform</span> Your Business?
      </h2>
      
      {/* Subtitle */}
      <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto leading-relaxed">
        Join 250+ visionary clients who trust us with their technology needs and digital transformation journey
      </p>
      
      {/* CTA Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 md:gap-6 justify-center animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
        <Link 
          to="/contact"
          className="group relative px-8 py-4 md:px-10 md:py-5 bg-gradient-to-r from-blue-600 to-purple-600 
                    text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 
                    hover:scale-105 active:scale-95 transition-all duration-300 
                    shadow-xl hover:shadow-2xl shadow-blue-600/20 dark:shadow-blue-900/30 
                    flex items-center justify-center overflow-hidden"
          aria-label="Get started today"
        >
          {/* Button Glow Effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 opacity-0 
                          group-hover:opacity-100 transition-opacity duration-500 blur-xl"></div>
          
          <span className="text-lg md:text-xl relative z-10">Get Started Today</span>
          <ArrowRight className="ml-3 w-5 h-5 md:w-6 md:h-6 relative z-10 group-hover:translate-x-2 transition-transform" />
        </Link>
        
        <Link 
          to="/portfolio"
          className="group relative px-8 py-4 md:px-10 md:py-5 bg-white/80 dark:bg-gray-800/80 
                    backdrop-blur-sm text-gray-900 dark:text-white 
                    rounded-xl font-semibold hover:bg-white dark:hover:bg-gray-700 
                    hover:scale-105 active:scale-95 transition-all duration-300 
                    shadow-xl hover:shadow-2xl border border-gray-200/50 dark:border-gray-700/50 
                    flex items-center justify-center"
          aria-label="View our portfolio"
        >
          <span className="text-lg md:text-xl">View Our Work</span>
          <ChevronDown className="ml-3 w-5 h-5 md:w-6 md:h-6 group-hover:translate-y-1 transition-transform" />
        </Link>
      </div>
      
      {/* Stats Row */}
      <div className="mt-12 grid grid-cols-3 gap-4 max-w-md mx-auto">
        <div className="text-center">
          <div className="text-2xl md:text-3xl font-bold text-blue-600 dark:text-blue-400">50+</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Projects</div>
        </div>
        <div className="text-center">
          <div className="text-2xl md:text-3xl font-bold text-purple-600 dark:text-purple-400">250+</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Clients</div>
        </div>
        <div className="text-center">
          <div className="text-2xl md:text-3xl font-bold text-pink-600 dark:text-pink-400">15+</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Experts</div>
        </div>
      </div>
      
      {/* Trust Badge */}
      <div className="mt-8 flex items-center justify-center space-x-2 text-gray-500 dark:text-gray-400 text-sm">
        <Shield className="w-4 h-4" />
        <span>Trusted by startups and enterprises worldwide</span>
      </div>
      
    </div>
  </section>
      {/* ===================================================================
          Custom Animations (Inline Styles)
          =================================================================== */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(40px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes float {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
          }
          33% {
            transform: translateY(-20px) rotate(5deg);
          }
          66% {
            transform: translateY(10px) rotate(-5deg);
          }
        }
        
        @keyframes pulse-glow {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }
        
        .animate-fade-in-up {
          animation: fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards;
          opacity: 0;
        }
        
        .animate-fade-in {
          animation: fadeIn 0.6s ease-out forwards;
          opacity: 0;
        }
        
        .animate-slide-in-left {
          animation: slideInLeft 0.6s ease-out forwards;
          opacity: 0;
        }
        
        .animate-slide-in-up {
          animation: slideInUp 0.6s ease-out forwards;
          opacity: 0;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-pulse-glow {
          animation: pulse-glow 2s ease-in-out infinite;
        }

        /* Mobile Optimization */
        @media (max-width: 768px) {
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          .text-6xl {
            font-size: 3rem;
          }
          
          .text-5xl {
            font-size: 2.5rem;
          }
          
          .text-4xl {
            font-size: 2rem;
          }
          
          .text-3xl {
            font-size: 1.75rem;
          }
          
          .text-2xl {
            font-size: 1.5rem;
          }
        }

        /* Smooth scrolling */
        html {
          scroll-behavior: smooth;
        }

        /* Selection styling */
        ::selection {
          background: rgba(59, 130, 246, 0.3);
          color: inherit;
        }

        /* Reduced motion preference */
        @media (prefers-reduced-motion: reduce) {
          .animate-fade-in-up,
          .animate-fade-in,
          .animate-slide-in-left,
          .animate-slide-in-up,
          .animate-float,
          .animate-pulse,
          .animate-pulse-glow,
          .transform,
          .transition-all,
          .transition-transform,
          .transition-opacity,
          .transition-shadow {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>
    </div>
  );
}